import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { VehicleCard } from "../types/vtc";
import { ALL_AREAS, LEGACY_ALL_AREAS } from "../data/home";
import { FILTER_CATEGORIES, FILTER_ENERGIES, SORT_KEYS } from "../data/marketplace";
import { listVehicles } from "../lib/marketplaceApi";
import CityAutocomplete from "../components/ui/CityAutocomplete";
import type { SortKey } from "../data/marketplace";
import { useT } from "../i18n";
import { absoluteUrl, breadcrumbLd, useSeo } from "../lib/seo";
import { addDaysIso, cn, daysBetween, today } from "../lib/format";

import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import VehicleCardItem from "../components/home/VehicleCardItem";
import VehicleFilters from "../components/vehicles/VehicleFilters";
import type { Filters } from "../components/vehicles/VehicleFilters";

const stripInput =
  "h-8 rounded border-0 bg-surface px-2.5 text-sm text-ink outline-none focus:ring-1 focus:ring-brand";

type Limits = { weekly: number; deposit: number };

/** Slider maxima, rounded up so the top position means "All". */
const limitsOf = (vehicles: VehicleCard[]): Limits => ({
  weekly: Math.ceil(Math.max(10, ...vehicles.map((v) => v.weekly)) / 10) * 10,
  deposit: Math.ceil(Math.max(100, ...vehicles.map((v) => v.deposit)) / 100) * 100,
});

/** ?category= from the home page tiles is either a fuel or a body type. */
function initialFilters(category: string | null, limits: Limits): Filters {
  return {
    availableOnly: false,
    maxWeekly: limits.weekly,
    maxDeposit: limits.deposit,
    energies: category && (FILTER_ENERGIES as string[]).includes(category) ? [category] : [],
    transmissions: [],
    categories: category && FILTER_CATEGORIES.includes(category) ? [category] : [],
    makes: [],
  };
}

/** Accents and case don't matter when comparing cities ("creteil" = "Créteil"). */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function matches(v: VehicleCard, f: Filters, city: string): boolean {
  return (
    (city === ALL_AREAS || fold(v.city) === fold(city)) &&
    (!f.availableOnly || v.availableUnits > 0) &&
    v.weekly <= f.maxWeekly &&
    v.deposit <= f.maxDeposit &&
    (!f.energies.length || f.energies.includes(v.energy)) &&
    (!f.transmissions.length || f.transmissions.includes(v.transmission)) &&
    (!f.categories.length || f.categories.includes(v.category ?? "")) &&
    (!f.makes.length || f.makes.includes(v.brand))
  );
}

const SORTERS: Record<SortKey, (a: VehicleCard, b: VehicleCard) => number> = {
  default: (a, b) => Number(!!b.urgent) - Number(!!a.urgent),
  "price-asc": (a, b) => a.weekly - b.weekly,
  "price-desc": (a, b) => b.weekly - a.weekly,
  available: (a, b) => b.availableUnits - a.availableUnits,
};

/** /vehicles: published listings from GET /listings, filtered and sorted here. */
export default function Vehicles() {
  const t = useT();
  const [vehicles, setVehicles] = useState<VehicleCard[] | null>(null);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    listVehicles()
      .then(setVehicles)
      .catch(() => setError(true));
  }, []);

  // A city search gets its own title ("VTC car rental in Saint-Denis"); the canonical stays /vehicles
  const seoCity = (searchParams.get("city") ?? "").trim();
  useSeo({
    title: seoCity ? t.seo.vehicles.cityTitle(seoCity) : t.seo.vehicles.title,
    fullTitle: true, // "… – LE COIN VTC" is part of the title
    description: seoCity ? t.seo.vehicles.cityDescription(seoCity) : t.seo.vehicles.description,
    path: "/vehicles",
    jsonLd: [
      breadcrumbLd([[t.seo.breadcrumbHome, "/"], [t.seo.breadcrumbVehicles]]),
      ...(vehicles?.length
        ? [
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: t.seo.vehicles.title,
              numberOfItems: vehicles.length,
              itemListElement: vehicles.slice(0, 30).map((v, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: absoluteUrl(`/vehicles/${v.slug}`),
                name: `${v.brand} ${v.model}`,
              })),
            },
          ]
        : []),
    ],
  });

  if (!vehicles) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header activeHref="/vehicles" />
        <main className="mx-auto w-full max-w-330 flex-1 px-4 pt-6 pb-16 sm:px-6">
          {error ? (
            <p className="m-0 rounded-lg bg-surface px-6 py-14 text-center text-sm text-muted">{t.home.popularError}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-4/5 animate-pulse rounded-lg bg-surface" />
              ))}
            </div>
          )}
        </main>
        <Footer />
      </div>
    );
  }
  return <VehicleResults vehicles={vehicles} />;
}

function VehicleResults({ vehicles }: { vehicles: VehicleCard[] }) {
  const t = useT();
  const limits = useMemo(() => limitsOf(vehicles), [vehicles]);
  const makes = useMemo(() => [...new Set(vehicles.map((v) => v.brand))].sort(), [vehicles]);
  const [params, setParams] = useSearchParams();
  const cityParam = params.get("city") ?? ALL_AREAS;
  const city = LEGACY_ALL_AREAS.includes(cityParam) ? ALL_AREAS : cityParam;
  const start = params.get("start") ?? today();
  const end = params.get("end") ?? addDaysIso(start, 1); // default: one day

  const [filters, setFilters] = useState(() => initialFilters(params.get("category"), limits));
  const [sort, setSort] = useState<SortKey>("default");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // "" (every location) leaves the city out of the URL
  const setSearch = (changes: Record<string, string>) => {
    const next = { city, start, end, ...changes };
    setParams(next.city ? next : { start: next.start, end: next.end }, { replace: true });
  };

  const results = vehicles.filter((v) => matches(v, filters, city)).sort(SORTERS[sort]);
  const nights = Math.max(0, daysBetween(start, end));
  const activeCount =
    Number(filters.availableOnly) +
    Number(filters.maxWeekly < limits.weekly) +
    Number(filters.maxDeposit < limits.deposit) +
    filters.energies.length +
    filters.transmissions.length +
    filters.categories.length +
    filters.makes.length;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header activeHref="/vehicles" />

      {/* Search strip: on phones the location takes a full row, the dates go below */}
      <div className="border-b border-line">
        <div className="mx-auto flex max-w-330 flex-wrap items-center gap-2 px-4 py-2.5 sm:px-6" role="group" aria-label={t.vehicles.dates}>
          <CityAutocomplete
            value={city}
            onChange={(c) => setSearch({ city: c })}
            ariaLabel={t.vehicles.area}
            className="w-full sm:w-56 sm:flex-none"
            inputClassName={cn(stripInput, "w-full")}
          />
          <input
            type="date"
            aria-label={t.vehicles.startDate}
            value={start}
            onChange={(e) => setSearch({ start: e.target.value, end: e.target.value >= end ? addDaysIso(e.target.value, 1) : end })}
            className={cn(stripInput, "min-w-0 flex-1 sm:flex-none")}
          />
          <span className="text-xs text-faint">→</span>
          <input
            type="date"
            aria-label={t.vehicles.endDate}
            value={end}
            min={addDaysIso(start, 1)}
            onChange={(e) => setSearch({ end: e.target.value })}
            className={cn(stripInput, "min-w-0 flex-1 sm:flex-none")}
          />
          <span className="ml-1 text-xs text-muted">{t.common.days(nights)}</span>
        </div>
      </div>

      <main className="mx-auto grid w-full max-w-330 flex-1 items-start gap-6 px-4 pt-4 pb-16 sm:px-6 lg:grid-cols-[192px_minmax(0,1fr)] lg:gap-8">
        <aside>
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            className="h-9 w-full cursor-pointer rounded border-0 bg-surface text-sm font-medium lg:hidden"
          >
            {filtersOpen ? t.vehicles.hideFilters : t.vehicles.filters(activeCount)}
          </button>
          <div className={cn("mt-3 lg:mt-0 lg:block", !filtersOpen && "hidden")}>
            <VehicleFilters
              filters={filters}
              limits={limits}
              makes={makes}
              onChange={setFilters}
              onReset={() => setFilters(initialFilters(null, limits))}
            />
          </div>
        </aside>

        <section>
          <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-2.5">
              <h1 className="m-0 text-lg font-medium">{city === ALL_AREAS ? t.vehicles.titleAll : t.vehicles.titleCity(city)}</h1>
              <span className="text-sm text-muted">{t.common.vehicles(results.length)}</span>
            </div>
            <select
              aria-label={t.vehicles.sort}
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="h-8 rounded border-0 bg-surface px-2.5 text-xs text-ink outline-none focus:ring-1 focus:ring-brand"
            >
              {SORT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t.vehicles.sortOptions[key]}
                </option>
              ))}
            </select>
          </div>

          {results.length ? (
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 xl:grid-cols-3">
              {results.map((v) => (
                <VehicleCardItem key={v.id} vehicle={v} search={`?start=${start}&end=${end}`} compact />
              ))}
            </div>
          ) : (
            <div className="rounded-lg bg-surface px-6 py-14 text-center">
              <h2 className="m-0 mb-1.5 text-lg font-medium">{t.vehicles.noMatchTitle}</h2>
              <p className="m-0 mb-4 text-sm text-muted">{t.vehicles.noMatchText}</p>
              <div className="flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilters(initialFilters(null, limits));
                    setSearch({ city: ALL_AREAS });
                  }}
                  className="h-10 cursor-pointer rounded border-0 bg-brand px-4.5 text-sm font-medium text-white hover:bg-brand-dark"
                >
                  {t.vehicles.resetFilters}
                </button>
                {city !== ALL_AREAS && (
                  <button
                    type="button"
                    onClick={() => setSearch({ city: ALL_AREAS })}
                    className="h-10 cursor-pointer rounded border-0 bg-white px-4.5 text-sm font-medium text-ink hover:bg-surface-hover"
                  >
                    {t.vehicles.allAreas}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
